import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, DynamicFilterValues, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import {
  PoSettlementDisplayStatus,
  PoSettlementRow,
  PoSettlementSummary,
  PurchaseOrderPaymentStatus,
  PurchaseOrderSettlementSearch,
  PurchaseOrderType,
} from '../../../models/po-settlement/po-settlement';
import { PoSettlementProxyService } from '../../../proxies/po-settlement.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { ProviderFilterService } from '../../../services/provider-filter.service';

@Component({
  selector: 'app-po-settlement',
  templateUrl: './po-settlement.component.html',
  styleUrl: './po-settlement.component.scss',
})
export class PoSettlementComponent {
  private readonly router = inject(Router);
  private readonly service = inject(PoSettlementProxyService);
  private readonly enumService = inject(EnumService);
  private readonly providerFilter = inject(ProviderFilterService);
  private readonly translateService = inject(TranslateService);

  private lastSearchCriteria: PurchaseOrderSettlementSearch = {};
  private currencyLabels: Record<number, string> = {};

  isLoading = signal(0);
  enumsReady = signal(false);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'po-settlement-filters', config: this.filterConfig });
  rows = signal<PoSettlementRow[]>([]);
  summary = signal<PoSettlementSummary | null>(null);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  /** This screen's own type-ahead state — see ProviderFilterService.createConfig. */
  private readonly providerAutocompleteConfig = this.providerFilter.createConfig(inject(DestroyRef));

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        if (!this.enumService.enumsReady()) return;
        this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
        this.enumsReady.set(true);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'purchaseOrderNumber',
          label: this.t('PoSettlement_PoNumber', 'PO #'),
          placeholder: this.t('PoSettlement_PoNumber_Placeholder', 'Enter PO number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'providerReference',
          label: this.t('ApPayment_Provider', 'Provider'),
          placeholder: this.t('Provider_Search', 'Search provider'),
          autocompleteConfig: this.providerAutocompleteConfig,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'paymentStatus',
          label: this.t('PoSettlement_PaymentStatus', 'Payment status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: this.paymentStatusOptions(),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'type',
          label: this.t('PoSettlement_Type', 'Type'),
          placeholder: this.t('Select_type', 'Select type'),
          options: this.typeOptions(),
          showClear: true,
          defaultVisible: false,
        },
      ],
    };
  }

  private paymentStatusOptions(): Record<number, string> {
    return {
      [PurchaseOrderPaymentStatus.Unpaid]: this.t('PoPaymentStatus_Unpaid', 'Unpaid'),
      [PurchaseOrderPaymentStatus.PartiallyPaid]: this.t('PoPaymentStatus_PartiallyPaid', 'Partially Paid'),
      [PurchaseOrderPaymentStatus.Paid]: this.t('PoPaymentStatus_Paid', 'Paid'),
      [PurchaseOrderPaymentStatus.Settled]: this.t('PoPaymentStatus_Settled', 'Settled'),
    };
  }

  private typeOptions(): Record<number, string> {
    return {
      [PurchaseOrderType.Standard]: this.t('PoType_Standard', 'Standard'),
      [PurchaseOrderType.Yarn]: this.t('PoType_Yarn', 'Yarn'),
      [PurchaseOrderType.Chemicals]: this.t('PoType_Chemicals', 'Chemicals'),
      [PurchaseOrderType.Administrative]: this.t('PoType_Administrative', 'Administrative'),
    };
  }

  private buildCriteria(filters: Partial<Record<string, unknown>>): PurchaseOrderSettlementSearch {
    const paymentStatus = filters['paymentStatus'];
    const type = filters['type'];
    return {
      purchaseOrderNumber: (filters['purchaseOrderNumber'] as string) || null,
      providerReference: this.providerFilter.toProviderId(filters['providerReference']),
      paymentStatus: paymentStatus != null && paymentStatus !== '' ? [Number(paymentStatus)] : null,
      type: type != null && type !== '' ? Number(type) : null,
    };
  }

  private loadSummary(criteria: PurchaseOrderSettlementSearch): void {
    this.startLoading();
    this.service
      .getSummary(criteria)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.summary.set(response?.data ?? null),
        error: () => this.summary.set(null),
      });
  }

  private search(criteria: PurchaseOrderSettlementSearch): void {
    this.lastSearchCriteria = criteria;
    this.startLoading();
    this.service
      .search(criteria, this.currentPage(), this.pageSize())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.rows.set(response?.data?.items ?? []);
          this.totalRecords.set(response?.data?.totalCount ?? 0);
        },
        error: () => {
          this.rows.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const criteria = this.buildCriteria(filters);
    this.currentPage.set(1);
    this.search(criteria);
    this.loadSummary(criteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.lastSearchCriteria);
  }

  redirectToDetails(purchaseOrderId?: string): void {
    if (!purchaseOrderId) return;
    this.router.navigate(['/po-settlement-details'], { queryParams: { id: purchaseOrderId } });
  }

  getRowData(rowData: unknown): PoSettlementRow {
    return rowData as PoSettlementRow;
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  displayStatusLabel(status: PoSettlementDisplayStatus): string {
    switch (status) {
      case PoSettlementDisplayStatus.Settled:
        return this.t('PoSettlementStatus_Settled', 'Settled');
      case PoSettlementDisplayStatus.PartiallyPaid:
        return this.t('PoSettlementStatus_PartiallyPaid', 'Partially Paid');
      case PoSettlementDisplayStatus.PaymentRequested:
        return this.t('PoSettlementStatus_PaymentRequested', 'Payment Requested');
      case PoSettlementDisplayStatus.Unpaid:
        return this.t('PoSettlementStatus_Unpaid', 'Unpaid');
      default:
        return this.t('PoSettlementStatus_NoDocuments', 'No AP documents yet');
    }
  }

  displayStatusSeverity(status: PoSettlementDisplayStatus): 'success' | 'warn' | 'info' | 'secondary' {
    switch (status) {
      case PoSettlementDisplayStatus.Settled:
        return 'success';
      case PoSettlementDisplayStatus.PartiallyPaid:
        return 'warn';
      case PoSettlementDisplayStatus.PaymentRequested:
        return 'info';
      default:
        return 'secondary';
    }
  }
}
